import {
  encodeWithQualityCeiling,
  resolveCaptureScale,
  resolveCaptureBackground,
  resolveLongEdgeDimensions,
  resolveScrollOffsetStyle,
} from '@/lib/feedback/screenshot'

type CaptureOptions = {
  /** Her `await` sonrası sorulur; true ise yakalama bırakılır ve `null` döner. */
  isCancelled?: () => boolean
}

/**
 * Görünür alanı (viewport) JPEG data URL olarak yakalar. Destek penceresi açılırken
 * (provider) ve sohbette "Ekran görüntüsü al" ile (pencere geçici gizlenerek) aynı
 * mantık kullanılır. Hata durumunda fırlatır; iptalde `null` döner.
 */
export async function captureViewportScreenshot(
  { isCancelled }: CaptureOptions = {},
): Promise<string | null> {
  const { domToCanvas } = await import('modern-screenshot')
  if (isCancelled?.()) return null
  // #727: yalnız görünür alanı, cihaz piksel oranında (en az 1x, en fazla
  // 2x) yakala. html2canvas CSS'i kendisi yeniden çizdiği için harf aralığı,
  // satır yüksekliği ve SVG ikonlarda sayfadan farklı sonuç veriyordu
  // (başlıklar iki satıra bölünüyor, yazıların altı kesiliyordu).
  // modern-screenshot SVG foreignObject ile çizimi tarayıcının kendisine
  // yaptırır — görüntü ekrandakiyle aynıdır (canlı karşılaştırma 2026-09-14).
  const scale = resolveCaptureScale(
    typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
  )
  // #1420: kaydırma, gövdeye dönüşüm vermek yerine negatif kenar boşluğuyla telafi edilir
  // (dönüşüm kaydırılmış sayfada ekranı boş/siyah bırakıyordu); zemin rengi her zaman verilir.
  const scrollStyle = resolveScrollOffsetStyle(window.scrollX, window.scrollY)
  const canvas = await domToCanvas(document.body, {
    scale,
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: resolveCaptureBackground([
      window.getComputedStyle(document.body).backgroundColor,
      window.getComputedStyle(document.documentElement).backgroundColor,
    ]),
    ...(scrollStyle ? { style: scrollStyle } : {}),
  })
  if (isCancelled?.()) return null

  // Uzun kenar tavanı (2560px) aşılırsa oranı koruyarak küçük bir ikinci
  // canvas'a çiz; aksi halde orijinal canvas'ı kullan.
  const target = resolveLongEdgeDimensions({ width: canvas.width, height: canvas.height })
  let sourceCanvas: { toDataURL: (type?: string, quality?: number) => string } = canvas
  if (target.scaled) {
    const resizedCanvas = document.createElement('canvas')
    resizedCanvas.width = target.width
    resizedCanvas.height = target.height
    const ctx = resizedCanvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, target.width, target.height)
      sourceCanvas = resizedCanvas
    }
  }

  // JPEG kalitesi 0.9'dan başlar; yalnız data URL boyutu tavanı aşarsa
  // kademeli düşer (0.8, 0.7) — asla 0.5 ölçeğe dönmez.
  const { dataUrl } = encodeWithQualityCeiling(
    (quality) => sourceCanvas.toDataURL('image/jpeg', quality),
  )
  if (isCancelled?.()) return null
  return dataUrl
}
